import {Button, makeStyles, Typography} from '@material-ui/core';
import {isLoaded} from 'Core/models/Async';
import {
  clearQuoteDeclineDetails,
  QuoteActionSource,
  toggleQuoteActionConfirmationModal,
  toggleQuotesTabActionModal,
} from 'Quoting/ducks/quoteActions';
import {OpportunityQuote} from 'Quoting/models/DTOs/Opportunity';
import React, {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {themeColors} from 'Theme';
import {QuotingCard} from '../shared/QuotingCard/QuotingCard';
import {ProductSelection} from './ProductSelection';
import {QuoteActionMenu} from './QuoteActions/QuoteActionMenu';
import {QuotesTabModal} from './QuoteActions/QuoteActionModals';
import {getCommonActionsForSelectedQuotes} from './QuoteActions/QuoteActionUtils';
import {QuotesList} from './QuoteList';
import {QuoteListFilter} from './QuoteListFilter';
import {validateNumberOfLives} from 'Quoting/utils/validationHelpers';
import {safeRenderValue} from 'Quoting/utils/utilities';

const useQuoteActionsStyles = makeStyles(() => ({
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '2rem',
  },
  createQuoteBtn: {
    border: 'none',
    marginRight: '0.5rem',
    background: themeColors.brightBlue_100,
    color: themeColors.blue_600,
    fontWeight: 600,
    '&:hover': {
      border: 'none',
      backgroundColor: themeColors.brightBlue_100,
      color: themeColors.blue_600,
    },
    textTransform: 'capitalize',
    padding: '0.75rem 1.5rem',
  },
  createQuoteError: {
    display: 'inline-flex',
    marginRight: '0.5rem',
  },
  proposalBtn: {
    marginLeft: '0.5rem',
    backgroundColor: themeColors.blue_400,
    color: themeColors.primaryWhite,
  },
  actionBtn: {
    backgroundColor: themeColors.blue_400,
    color: themeColors.primaryWhite,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: themeColors.brightBlue_300,
      color: themeColors.primaryWhite,
    },
    textTransform: 'capitalize',
    padding: '0.75rem 1.5rem',
  },
}));

type QuoteTabActionsProps = {
  onCreateQuote: () => void;
};

export const QuotesTabActions = ({onCreateQuote}: QuoteTabActionsProps) => {
  const {availableQuoteActionsMap, opportunitySummary} = useSelector((state: ReducerState) => state.opportunity);
  const {selectedQuotesForAction} = useSelector((state: ReducerState) => state.quoteActions);
  const selectedQuotes = useMemo(() => Object.values(selectedQuotesForAction), [selectedQuotesForAction]);

  const selectedQuoteActions =
    Array.isArray(selectedQuotes) &&
    selectedQuotes.length > 0 &&
    selectedQuotes.every(quote => quote.status === selectedQuotes[0].status) &&
    availableQuoteActionsMap.key === 'Loaded'
      ? getCommonActionsForSelectedQuotes(
          selectedQuotes.map(quote => availableQuoteActionsMap.Data[quote.rootId]),
          selectedQuotes.length
        )
      : [];

  const classes = useQuoteActionsStyles();

  const numberOfEligibleLives =
    opportunitySummary.key === 'Loaded' ? opportunitySummary?.Data?.numberOfEligibleLives : undefined;

  const [error, setError] = useState<string | null>(null);

  const handleCreateQuote = () => {
    const numberOfLivesValidationResult = validateNumberOfLives(numberOfEligibleLives);
    if (numberOfLivesValidationResult?.error) {
      setError(numberOfLivesValidationResult?.errorMessage);
      return;
    }
    setError(null);
    onCreateQuote();
  };

  return isLoaded(opportunitySummary) ? (
    <div className={classes.actionsContainer}>
      <div>
        <QuoteListFilter />
      </div>
      <div>
        {error && (
          <div className={classes.createQuoteError}>
            <Typography variant="body2" color="error">
              {safeRenderValue(error)}
            </Typography>
          </div>
        )}
        <Button onClick={handleCreateQuote} className={classes.createQuoteBtn} variant="outlined" color="primary">
          Create a New Quote
        </Button>
        <QuoteActionMenu
          availableActions={selectedQuoteActions}
          actionMenuBtnVariant="button"
          className={classes.actionBtn}
          selectedQuotes={selectedQuotes}
          source={QuoteActionSource.ActionBtn}
        />
      </div>
    </div>
  ) : null;
};

export type SelectedQuoteDetails = {
  rootId: string;
  productModel: string;
  revisionNo: number;
  status: string;
  policyNumber?: string;
  effectiveDate?: string;
};

export const QuotesTab = ({filteredQuotes}: {filteredQuotes: OpportunityQuote[]}) => {
  const [showProductSelection, setShowProductSelection] = useState(false);
  const {isQuoteActionModalOpen, selectedQuoteAction, isQuoteActionConfirmationModalOpen} = useSelector(
    (state: ReducerState) => state.quoteActions
  );
  const dispatch = useDispatch();

  return (
    <div>
      <QuotesTabActions onCreateQuote={() => setShowProductSelection(true)} />
      {showProductSelection ? (
        <QuotingCard title="Select Product">
          <ProductSelection onCancel={() => setShowProductSelection(false)} />
        </QuotingCard>
      ) : (
        <QuotesList filteredQuotes={filteredQuotes} />
      )}
      <QuotesTabModal
        action={selectedQuoteAction}
        key={`quote-action-modal-${selectedQuoteAction}`}
        open={isQuoteActionModalOpen}
        onClose={() => dispatch(toggleQuotesTabActionModal(false))}
      />
      <QuotesTabModal
        action="ConfirmQuoteAction"
        key="action-confirmation-modal"
        open={isQuoteActionConfirmationModalOpen}
        onClose={() => {
          dispatch(clearQuoteDeclineDetails());
          dispatch(toggleQuoteActionConfirmationModal(false));
        }}
      />
    </div>
  );
};
